import { create } from "zustand";
import { persist } from "zustand/middleware";
import { v4 as uuidv4 } from "uuid";
import { normalizeProductName } from "../../../features/onboarding/utils/normalize-product-name";

const PRODUCT_ID_OCR_DOCUMENTOS =
	process.env.REACT_APP_PRODUCT_ID_OCR_DOCUMENTOS;
const PRODUCT_ID_OCR_CNH_SERPRO =
	process.env.REACT_APP_PRODUCT_ID_OCR_CNH_SERPRO;
const PRODUCT_ID_CNH_SERPRO = process.env.REACT_APP_PRODUCT_ID_CNH_SERPRO;

const keysValidations: { [key: string]: string } = {
	"20": "selfie",
	"21": "cnh",
	input: "input",
};

export type DraggableItem = {
	id?: string;
	key?: string;
	id_choice?: string;
	type?: string;
	label?: string;
	required?: boolean;
	error?: string;
	options?: string[];
	description?: string;
	validations?: string[] | boolean;
	length_label?: number;
	length_value?: number;
	price?: number;
	onDragEnd?: (item: DraggableItem) => void;
};

export type ValidationItem = {
	id: number;
	name: string;
	price: string;
	description: string;
	category: string;
	available: boolean;
};

type ValidationResult = Record<string, ValidationItem[]>;

type CheckPermissionOnboarding = {
	token: string;
	using_onboarding: boolean;
	permission_onboarding: boolean;
};

type FormState = {
	checkPermissionOnboarding: CheckPermissionOnboarding;
	fileProcessing: boolean;
	items: DraggableItem[];
	price_onboarding: Record<string, string>;
	basePrice: number;
	totalPrice: number;
	optionsValues: Record<string, string[]>;
	inputOptionValues: Record<string, string>;
	validations: Record<string, string[]>;
	validationsOptions: Record<string, ValidationItem[]>;
	priceValidations: Record<string, number>;
	setCheckPermissionOnboarding: (
		checkPermissionOnboarding: CheckPermissionOnboarding,
	) => void;
	addItems: (item: DraggableItem) => void;
	calculatePrice: () => void;
	getPrice_onboarding: (
		price_onboarding_update: Record<string, string>,
	) => void;
	removeItems: (key: string, remove: string, validations: any) => void;
	updateItems: (items: DraggableItem[], saved?: boolean) => void;
	setOptionsValues: (key: string, options: string[]) => void;
	setInputOptionValue: (key: string, value: string) => void;
	clearFormValues: () => void;
	setFileProcessing: (newState: boolean) => void;
	toggleValidation: (validationResult: ValidationResult) => void;
	toggleAddValidation: (
		type: string,
		typeToRemove: string | boolean,
		key?: string,
	) => void;
	updateValidation: (
		key: string,
		type: string,
		idsValidations: string[],
	) => void;
	toggleRemoveValidation: (
		type: string,
		idValidation: string | boolean,
		addOrRemoveValidation: boolean,
		key: string,
	) => void;
	getValidationsToSend: (category: "2" | "4") => string[];
	getValidationSelection: (category: "2" | "4") => {
		toSend: string[];
		unavailable: string[];
	};
};

export const useItemsStore = create<FormState>()(
	persist(
		(set, get) => ({
			checkPermissionOnboarding: {
				token: "",
				using_onboarding: false,
				permission_onboarding: false,
			},
			fileProcessing: false,
			items: [],
			price_onboarding: {},
			basePrice: 2.8,
			totalPrice: 2.8,
			optionsValues: {},
			inputOptionValues: {},
			validations: {},
			priceValidations: {
				input: 0,
				field: 0,
			},
			validationsOptions: {},

			setCheckPermissionOnboarding: (checkPermissionOnboarding) =>
				set({ checkPermissionOnboarding }),

			addItems: (item) => {
				const keyRandom = uuidv4();
				set((state) => ({
					items: [
						...state.items,
						{
							id: item.id,
							key: keyRandom,
							id_choice: item.id_choice,
							type: item.type,
							label: item.label,
							required: item.required || false,
							error: item.error,
							options: item.options || [],
							description: item.description,
							validations: item.validations,
							length_label: item.length_label,
							length_value: item.length_value,
							price: item.price,
						},
					],
				}));

				if (
					item.validations === true ||
					(Array.isArray(item.validations) && item.validations.length > 0)
				) {
					const type = item.type!;
					get().toggleAddValidation(type, false, keyRandom);
				}

				get().calculatePrice();
			},

			updateItems: (updatedItems, saved) => {
				if (saved) {
					set((state) => {
						// --- 1) Reconstrói as validações ativas
						const newValidations = { ...state.validations };
						updatedItems.forEach((itm) => {
							if (
								Array.isArray(itm.validations) &&
								itm.validations.length > 0
							) {
								newValidations[itm.key!] = itm.validations!;
							} else {
								delete newValidations[itm.key!];
							}
						});

						// --- 2) Soma validações de input agrupadas e só PF por grupo
						const allOpts = state.validationsOptions.input || [];
						const grouped = (newValidations["input"] ?? []).reduce<
							Record<string, ValidationItem[]>
						>((acc, id) => {
							const it = allOpts.find((v) => String(v.id) === String(id));
							if (!it) return acc;
							const nameKey = normalizeProductName(it.name);
							(acc[nameKey] ??= []).push(it);
							return acc;
						}, {});
						const inputSum = Object.values(grouped).reduce((sum, arr) => {
							const pf = arr.find((i) => i.category === "2");
							return sum + (pf ? parseFloat(pf.price.replace(",", ".")) : 0);
						}, 0);

						// --- 3) Soma validações de field (todos os itens que têm validations)
						const fieldSum = updatedItems.reduce((sumAll, itm) => {
							if (
								Array.isArray(itm.validations) &&
								itm.validations.length > 0
							) {
								const keyVal = keysValidations[itm.type!]; // 'selfie', 'cnh', etc.
								const opts = state.validationsOptions[keyVal] || [];
								const sumItem = itm.validations!.reduce((s, id) => {
									const opt = opts.find((v) => String(v.id) === String(id));
									return (
										s + (opt ? parseFloat(opt.price.replace(",", ".")) : 0)
									);
								}, 0);
								return sumAll + sumItem;
							}
							return sumAll;
						}, 0);

						return {
							items: updatedItems,
							validations: newValidations,
							priceValidations: {
								input: inputSum,
								field: fieldSum,
							},
						};
					});
				} else {
					set((state) => ({ items: updatedItems }));
				}
				// único recalculo final
				get().calculatePrice();
			},

			removeItems: (key, type, validation) => {
				set((state) => ({
					items: state.items.filter((item) => item.key !== key),
				}));

				if (type === "20" || type === "21") {
					const hasValidation =
						validation === true ||
						(Array.isArray(validation) && validation.length > 0);
					if (hasValidation) {
						get().toggleAddValidation(type, true, key);
					}

					get().calculatePrice();
					return;
				}
				get().calculatePrice();
			},

			getPrice_onboarding: (price_onboarding_update) => {
				const convertedPrices = Object.fromEntries(
					Object.entries(price_onboarding_update).map(([key, value]) => [
						key,
						value.replace(",", "."),
					]),
				);

				set({
					price_onboarding: convertedPrices,
					basePrice: Number(convertedPrices.price_default),
					totalPrice: Number(convertedPrices.price_default),
				});
			},

			calculatePrice: () => {
				const { items, priceValidations, price_onboarding } = get();

				const choicesTextFields = ["0", "3", "4", "5", "6", "8"];
				const ChoicesAttachmentsFields = ["1", "2"];

				// Selfie (20) e CNH (21) passam a contar no excedente de anexos
				const isAttachmentLike = (item: DraggableItem) =>
					ChoicesAttachmentsFields.includes(item.id_choice!) ||
					item.type === "20" ||
					item.type === "21";

				const textFields = items.filter((item) =>
					choicesTextFields.includes(item.id_choice!),
				);

				const attachments = items.filter(isAttachmentLike);

				const textFieldsExcess =
					textFields.length > 15 ? textFields.slice(15) : [];

				const filteredAttachments = attachments.filter(
					(item) => item.type !== "19",
				);

				const attachmentsExcess =
					filteredAttachments.length > 5 ? filteredAttachments.slice(5) : [];

				const signatureItems = attachments.filter((item) => item.type === "19");

				const additionalTextPrice =
					textFieldsExcess.length *
					parseFloat(price_onboarding.textField?.replace(",", ".") || "0");
				const additionalAttachmentsPrice =
					attachmentsExcess.length *
					parseFloat(price_onboarding.file?.replace(",", ".") || "0");
				const signaturePrice =
					signatureItems.length *
					parseFloat(price_onboarding.fieldSiganture?.replace(",", ".") || "0");

				const newTotalPrice =
					Number(price_onboarding.price_default) +
					additionalTextPrice +
					additionalAttachmentsPrice +
					signaturePrice;

				const totalPrices =
					newTotalPrice + priceValidations["input"] + priceValidations["field"];

				set({ totalPrice: totalPrices });
			},

			setOptionsValues: (key, options) =>
				set((state) => ({
					optionsValues: { ...state.optionsValues, [key]: options },
				})),

			setInputOptionValue: (key, value) =>
				set((state) => ({
					inputOptionValues: { ...state.inputOptionValues, [key]: value },
				})),

			toggleValidation: (validation) =>
				set((state) => ({
					validationsOptions: {
						...state.validationsOptions,
						...validation,
					},
				})),

			toggleAddValidation: (type, typeToRemove, key) =>
				set((state) => {
					const keyValidation = keysValidations[type];
					const validationsOpts = state.validationsOptions[keyValidation] || [];

					// Atualiza state.validations conforme add/remove do item
					if (!typeToRemove) {
						state.validations = {
							...state.validations,
							[key!]: validationsOpts.map((v) => String(v.id)),
						};
					} else {
						const nv = { ...state.validations };
						delete nv[key!];
						state.validations = nv;
					}

					// Recalcula INPUT via unificação por grupo e aplica só o delta
					const prevInput = state.priceValidations.input || 0;
					const pfIds = get().getValidationsToSend("2"); // regra de grupo
					const optsInput = state.validationsOptions.input || [];
					const nextInput = pfIds.reduce((sum, id) => {
						const it = optsInput.find((o) => String(o.id) === id);
						return sum + (it ? parseFloat(it.price.replace(",", ".")) : 0);
					}, 0);
					const inputDelta = nextInput - prevInput;
					state.priceValidations.input = nextInput;

					// Recalcula FIELD deste item (selfie/cnh) e aplica só o delta
					const fieldDelta =
						validationsOpts.reduce((acc, v) => {
							const p = parseFloat(v.price.replace(",", ".")) || 0;
							return acc + p;
						}, 0) * (typeToRemove ? -1 : 1);
					state.priceValidations.field =
						(state.priceValidations.field || 0) + fieldDelta;

					// Total = total anterior + deltas (sem tocar anexos aqui)
					const newTotal = state.totalPrice + inputDelta + fieldDelta;
					return { ...state, totalPrice: Math.max(state.basePrice, newTotal) };
				}),

			updateValidation: (keyValidation, itemType, idsValidations) =>
				set((state) => {
					const newValidations = {
						...state.validations,
						[keyValidation]: idsValidations,
					};

					// --- 2) decide input vs field ---
					const keyPrice =
						itemType === "20" || itemType === "21" ? "field" : "input";
					const opts =
						state.validationsOptions[keysValidations[keyValidation]] || [];

					let sum = 0;
					if (keyPrice === "input") {
						// agrupamento PF-por-grupo
						const grouped = idsValidations.reduce<
							Record<string, ValidationItem[]>
						>((acc, id) => {
							const it = opts.find((v) => String(v.id) === id);
							if (!it) return acc;
							const nameKey = normalizeProductName(it.name);
							(acc[nameKey] ??= []).push(it);
							return acc;
						}, {});
						sum = Object.values(grouped).reduce((s, arr) => {
							const pf = arr.find((i) => i.category === "2");
							return s + (pf ? parseFloat(pf.price.replace(",", ".")) : 0);
						}, 0);
					} else {
						// soma todas as IDs para field (selfie, cnh, etc.)
						sum = idsValidations.reduce((s, id) => {
							const it = opts.find((v) => String(v.id) === id);
							return s + (it ? parseFloat(it.price.replace(",", ".")) : 0);
						}, 0);
					}

					// --- 3) atualiza priceValidations e totalPrice mantendo base+outros ---
					const newPriceValidations = {
						...state.priceValidations,
						[keyPrice]: sum,
					};
					const total =
						state.basePrice +
						newPriceValidations.input +
						newPriceValidations.field;

					return {
						...state,
						validations: newValidations,
						priceValidations: newPriceValidations,
						totalPrice: total,
					};
				}),

			toggleRemoveValidation: (
				type,
				idValidation,
				addOrRemoveValidation,
				key,
			) =>
				set((state) => {
					const keyValitadion = keysValidations[type];
					const validations = state.validationsOptions[keyValitadion] || [];

					const keyPriceValidations = key === "input" ? "input" : "field";

					if (!idValidation) return state;

					// Garante array
					if (!state.validations[key!]) state.validations[key!] = [];

					if (addOrRemoveValidation) {
						// ADD
						if (!state.validations[key!].includes(String(idValidation))) {
							// mantém sua regra especial de CNH+OCR
							const needsCnhOcrPair =
								idValidation === PRODUCT_ID_CNH_SERPRO &&
								!state.validations[key!].includes(
									String(PRODUCT_ID_OCR_DOCUMENTOS),
								);

							state.validations[key!].push(String(idValidation));
							if (needsCnhOcrPair) {
								state.validations[key!].push(String(PRODUCT_ID_OCR_CNH_SERPRO));
							}
						}
					} else {
						// REMOVE
						const hasCnhOcrPair =
							idValidation === PRODUCT_ID_CNH_SERPRO &&
							state.validations[key!].includes(
								String(PRODUCT_ID_OCR_CNH_SERPRO),
							);

						if (hasCnhOcrPair) {
							state.validations[key!] = state.validations[key!].filter(
								(id) => id !== String(PRODUCT_ID_OCR_CNH_SERPRO),
							);
						}
						state.validations[key!] = state.validations[key!].filter(
							(id) => id !== String(idValidation),
						);
					}

					// Para INPUT: recalcula sempre via unificação e aplica só o delta
					if (keyPriceValidations === "input") {
						const prev = state.priceValidations.input || 0;

						const pfIds = get().getValidationsToSend("2"); // sua função de unificação por grupo (PF)
						const opts = state.validationsOptions.input || [];

						const next = pfIds.reduce((sum, id) => {
							const it = opts.find((o) => String(o.id) === id);
							return sum + (it ? parseFloat(it.price.replace(",", ".")) : 0);
						}, 0);

						const delta = next - prev;

						state.priceValidations.input = next;

						return {
							...state,
							totalPrice: state.totalPrice + delta,
						};
					}

					// FIELD (Selfie/CNH): mantém a sua lógica atual por ID (delta por item)
					if (addOrRemoveValidation) {
						const priceStr =
							validations.find(
								(item) => String(item.id) === String(idValidation),
							)?.price || "0";
						const val = parseFloat(priceStr.replace(",", ".")) || 0;
						state.priceValidations.field = state.priceValidations.field + val;
						return { ...state, totalPrice: state.totalPrice + val };
					} else {
						const priceStr =
							validations.find(
								(item) => String(item.id) === String(idValidation),
							)?.price || "0";
						const val = parseFloat(priceStr.replace(",", ".")) || 0;
						state.priceValidations.field = state.priceValidations.field - val;
						const newTotal = Math.max(state.basePrice, state.totalPrice - val);
						return { ...state, totalPrice: newTotal };
					}
				}),

			clearFormValues: () =>
				set(() => ({
					items: [],
					optionsValues: {},
					inputOptionValues: {},
					validations: {},
					validationsOptions: {},
					validationsInput: [],
					totalPrice: 2.8,
					priceValidations: {
						input: 0,
						field: 0,
					},
				})),

			setFileProcessing: (newState) =>
				set(() => ({
					fileProcessing: newState,
				})),

			/**
			 * Agrupa as validações de input por nome “limpo” e retorna
			 * um só ID PF por grupo (category==='2') ou PJ (category==='4').
			 */
			getValidationsToSend: (category: "2" | "4"): string[] => {
				const opts = get().validationsOptions.input || [];
				const selIds = get().validations.input || [];

				// pega só os items selecionados
				const selItems = opts.filter((i) => selIds.includes(String(i.id)));

				// agrupa por nome sem “CPF”/“CNPJ”
				const groups = selItems.reduce<Record<string, ValidationItem[]>>(
					(acc, it) => {
						const key = normalizeProductName(it.name);
						(acc[key] ??= []).push(it);
						return acc;
					},
					{},
				);

				// de cada grupo, escolhe PF ou PJ
				return Object.values(groups)
					.map((group) => {
						const match = group.find((it) => it.category === category);
						return match ? String(match.id) : null;
					})
					.filter((id): id is string => id !== null);
			},

			/** Retorna tanto os IDs a enviar quanto os grupos sem validação disponível */
			getValidationSelection: (
				category: "2" | "4",
			): { toSend: string[]; unavailable: string[] } => {
				const opts = get().validationsOptions.input || [];
				const selIds = get().validations.input || [];

				// só os itens que o usuário marcou
				const selItems = opts.filter((i) => selIds.includes(String(i.id)));

				// agrupa por nome “limpo”
				const groups = selItems.reduce<Record<string, ValidationItem[]>>(
					(acc, it) => {
						const key = normalizeProductName(it.name);
						(acc[key] ??= []).push(it);
						return acc;
					},
					{},
				);

				// para cada grupo, decide se tem match (categoria + available)
				const toSend: string[] = [];
				const unavailable: string[] = [];

				Object.entries(groups).forEach(([name, items]) => {
					const match = items.find(
						(i) => i.category === category && i.available,
					);
					if (match) toSend.push(String(match.id));
					else unavailable.push(name);
				});

				return { toSend, unavailable };
			},
		}),
		{
			name: "onboarding-storage",
			partialize: (state) => ({
				checkPermissionOnboarding: state.checkPermissionOnboarding,
			}),
		},
	),
);
